/**
 * ObjectSerializer - binary encode/decode for arbitrary objects with @property metadata.
 *
 * Shared between the replication pipeline ({@link ReplicationSerializer}) and the RPC
 * binary codec ({@link RpcBinaryCodec}). Contains the per-object serialize/deserialize
 * logic and the class-metadata cache; higher-level actor/component coordination lives in
 * each consumer.
 */
import { Actor } from '../actors/Actor.js';
import { ClassRegistry } from '../systems/ClassRegistry.js';
import { getPropertyMetadata } from '../utils/serialization/decorator.js';

import {
  float16BitsToFloat,
  float32ToMonotonic,
  floatToFloat16Bits,
  monotonicToFloat32,
  readClassDiscriminator,
  writeClassDiscriminator,
} from './ReplicationBinaryCodec.js';

import type {
  BinaryReader,
  BinaryWriter
} from './ReplicationBinaryCodec.js';
import type { NetPropertyMetadata, PropertyMetadata, QuantizationMode } from '../utils/serialization/decorator.js';

// ---------------------------------------------------------------------------
// ActorRef marker (shared across serializers)
// ---------------------------------------------------------------------------

/**
 * Marker used in JS-value state records to represent an actor reference.
 */
export interface ActorRef {
  $actorRef: string;
}

/** Check if a value is an actor-reference marker. */
export function isActorRef(value: unknown): value is ActorRef {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof (value as Record<string, unknown>).$actorRef === 'string'
  );
}

// ---------------------------------------------------------------------------
// Class metadata cache
// ---------------------------------------------------------------------------

/**
 * Per-class cached metadata used by both encode and decode paths.
 */
export interface ClassObjectMetadata {
  /** Ordered list of replicated property names (base → derived). */
  replicatedProps: string[];
  /** propName → `onRep*` callback name. */
  repCallbacks: Map<string, string>;
  /** propName → binary net metadata. Only props with a `net` block are included. */
  netMeta: Map<string, NetPropertyMetadata>;
}

const classMetadataCache = new Map<Function, ClassObjectMetadata>();

/** Return (and cache) the object metadata for a given class constructor. */
export function getClassObjectMetadata(classConstructor: Function): ClassObjectMetadata {
  let meta = classMetadataCache.get(classConstructor);
  if (!meta) {
    meta = buildClassObjectMetadata(classConstructor);
    classMetadataCache.set(classConstructor, meta);
  }
  return meta;
}

function buildClassObjectMetadata(classConstructor: Function): ClassObjectMetadata {
  const allMetadata = getPropertyMetadata(classConstructor.prototype);
  const replicatedProps: string[] = [];
  const repCallbacks = new Map<string, string>();
  const netMeta = new Map<string, NetPropertyMetadata>();

  for (const [propName, metadata] of Object.entries(allMetadata)) {
    const pm = metadata as PropertyMetadata;
    if (pm?.replicate !== true) continue;

    if (!pm.net) {
      console.warn(
        `[ObjectSerializer] Property "${propName}" on ${classConstructor.name} has replicate:true but no net block. ` +
        'Run the property metadata generator or add a net block manually. Skipping binary encoding for this property.'
      );
      continue;
    }

    replicatedProps.push(propName);
    netMeta.set(propName, pm.net);

    const callbackName = `onRep${propName.charAt(0).toUpperCase()}${propName.slice(1)}`;
    if (typeof classConstructor.prototype[callbackName] === 'function') {
      repCallbacks.set(propName, callbackName);
    }
  }

  // Reverse so base-class properties come first (stable ordering across client/server).
  replicatedProps.reverse();

  return { replicatedProps, repCallbacks, netMeta };
}

// ---------------------------------------------------------------------------
// serializeObjectAndEncode
// ---------------------------------------------------------------------------

/**
 * Serialize all replicated properties of `obj` into `outState` (for next-tick delta
 * comparison) and encode changed ones into `writer` in a single pass.
 *
 * The bitmask is reserved as zero bytes at the start, individual values are written
 * inline as changes are detected, and the bitmask is patched in-place once all props
 * have been processed. Unchanged struct/class values are rolled back via `resetTo`.
 *
 * Numeric and quantized values are encoded as deltas (`new - old`). When a property
 * has no prior value in `lastState` (key absent / `undefined`), the delta base is `0`,
 * which is equivalent to sending the absolute value.
 *
 * @param writer          Binary output buffer.
 * @param obj             Object to serialize (actor, component, or struct instance).
 * @param lastState       JS-value snapshot from the previous tick.
 * @param outState        Output record that receives the current JS-value snapshot.
 * @param isOwningClient  Whether the target is the owning client; controls `replicateTo` filtering.
 * @returns `true` if at least one property changed and was written.
 */
export function serializeObjectAndEncode(
  writer: BinaryWriter,
  obj: object,
  lastState: Record<string, unknown>,
  outState: Record<string, unknown>,
  isOwningClient: boolean = true,
): boolean {
  const { replicatedProps, netMeta } = getClassObjectMetadata(obj.constructor);

  const bitmaskCursor = writer.reserveBitmask(replicatedProps.length);
  const changedIndices = new Set<number>();

  for (let i = 0; i < replicatedProps.length; i++) {
    const propName = replicatedProps[i];
    const net = netMeta.get(propName);
    if (!net) continue;

    const replicateTo = net.replicateTo ?? 'all';
    if (replicateTo === 'owner' && !isOwningClient) continue;
    if (replicateTo === 'non-owner' && isOwningClient) continue;

    const liveValue = (obj as Record<string, unknown>)[propName];
    const lastSer = lastState[propName];

    const { serialized, changed } = encodeValueCombined(writer, liveValue, lastSer, net);
    outState[propName] = serialized;
    if (changed) changedIndices.add(i);
  }

  writer.patchBitmask(bitmaskCursor, replicatedProps.length, changedIndices);
  return changedIndices.size > 0;
}

/**
 * Capture a JS-value snapshot of all replicated properties of `obj` into `outState`
 * without writing any binary output.
 *
 * Applies the same quantization as {@link serializeObjectAndEncode} so that the result
 * can be used as a `lastState` base for delta comparison. Used by
 * {@link ReplicationSerializer.getCdoSnapshot} to build a baseline without encoding.
 *
 * @param obj             Object to snapshot.
 * @param outState        Output record that receives the current JS-value snapshot.
 * @param isOwningClient  Controls `replicateTo` filtering, same as `serializeObjectAndEncode`.
 */
export function serializeObjectToState(
  obj: object,
  outState: Record<string, unknown>,
  isOwningClient: boolean = true,
): void {
  const { replicatedProps, netMeta } = getClassObjectMetadata(obj.constructor);

  for (let i = 0; i < replicatedProps.length; i++) {
    const propName = replicatedProps[i];
    const net = netMeta.get(propName);
    if (!net) continue;

    const replicateTo = net.replicateTo ?? 'all';
    if (replicateTo === 'owner' && !isOwningClient) continue;
    if (replicateTo === 'non-owner' && isOwningClient) continue;

    outState[propName] = snapshotValue((obj as Record<string, unknown>)[propName], net, isOwningClient);
  }
}

function snapshotValue(value: unknown, net: NetPropertyMetadata, isOwningClient: boolean = true): unknown {
  if (net.nullable && (value === null || value === undefined)) return null;

  switch (net.netType) {
    case 'bool': return value as boolean;
    case 'int':
    case 'uint': return typeof value === 'number' ? value : 0;
    case 'float': {
      const v = typeof value === 'number' ? value : 0;
      if (!net.quantization) return v;
      if (net.quantization.mode === 'float64') return v; // stored as absolute float
      return quantizeScalar(v, net.quantization);
    }
    case 'string': return typeof value === 'string' ? value : '';
    case 'enum': return value;
    case 'actorRef': {
      const uuid = value instanceof Actor ? value.uuid : (isActorRef(value) ? value.$actorRef : '');
      return { $actorRef: uuid } satisfies ActorRef;
    }
    case 'vector2': {
      const v = value as { x: number; y: number };
      if (net.quantization) {
        const q = net.quantization;
        return [quantizeScalar(v.x, q), quantizeScalar(v.y, q)];
      }
      return [v.x, v.y];
    }
    case 'vector3':
    case 'euler': {
      const v = value as { x: number; y: number; z: number };
      if (net.quantization) {
        const q = net.quantization;
        return [quantizeScalar(v.x, q), quantizeScalar(v.y, q), quantizeScalar(v.z, q)];
      }
      return [v.x, v.y, v.z];
    }
    case 'vector4':
    case 'quaternion': {
      const v = value as { x: number; y: number; z: number; w: number };
      if (net.quantization) {
        const q = net.quantization;
        if (q.mode === 'drop-w') return snapshotDropW(v, q.bits);
        return [quantizeScalar(v.x, q), quantizeScalar(v.y, q), quantizeScalar(v.z, q), quantizeScalar(v.w, q)];
      }
      return [v.x, v.y, v.z, v.w];
    }
    case 'struct':
    case 'class': {
      if (value === null || value === undefined) return null;
      const nestedOut: Record<string, unknown> = {};
      serializeObjectToState(value as object, nestedOut, isOwningClient);
      return nestedOut;
    }
    case 'array': {
      const arr = Array.isArray(value) ? value : [];
      if (!net.element) return arr.slice();
      return arr.map(elem => snapshotValue(elem, net.element!, isOwningClient));
    }
    default:
      return value;
  }
}

// ---------------------------------------------------------------------------
// decodeObjectFromBinary
// ---------------------------------------------------------------------------

// Sentinel returned for unresolved actor references; callers must NOT write it to the object.
const UNRESOLVED_SENTINEL: unique symbol = Symbol('UNRESOLVED_ACTOR_REF');

/**
 * Decode replicated properties from `reader` into `obj`, simultaneously building
 * a JS-value snapshot in `outState` that faithfully captures what was on the wire.
 *
 * - **Present properties**: decoded, applied to `obj`, and captured in `outState`.
 *   `actorRef` values are stored as `{ $actorRef: uuid }` even when unresolved.
 * - **Absent (unchanged) properties**: value is carried forward from `prevState`
 *   rather than reading the (possibly locally-predicted) live actor property.
 *
 * @param obj             Target object (actor, component, or struct instance).
 * @param prevState       JS-value snapshot from the previous received packet, or `null`
 *                        for a full-state decode (spawn). When `null`, every property is
 *                        expected to be present in the bitmask.
 * @param outState        Output record that receives the updated JS-value snapshot.
 * @param resolveActorRef Resolver for actor UUID → Actor instance.
 * @param pathPrefix      Dot-path prefix used for dangling-ref tracking keys.
 * @param unresolvedRefs  Output map of dotPath → UUID for unresolvable actor references.
 * @param pendingCallbacks Collected `onRep*` callbacks to fire after all properties are applied.
 */
export function decodeObjectFromBinary(
  reader: BinaryReader,
  obj: object,
  prevState: Record<string, unknown>,
  outState: Record<string, unknown>,
  resolveActorRef: ((uuid: string) => Actor | null) | undefined,
  pathPrefix: string,
  unresolvedRefs: Map<string, string>,
  pendingCallbacks: Array<[object, string]>,
): void {
  const metadata = getClassObjectMetadata(obj.constructor);
  const { replicatedProps, netMeta } = metadata;

  const present = reader.readBitmask(replicatedProps.length);

  for (let i = 0; i < replicatedProps.length; i++) {
    const propName = replicatedProps[i];
    const net = netMeta.get(propName);
    if (!net) continue;

    if (!present[i]) {
      // Unchanged — carry forward from the confirmed prev snapshot so that
      // locally-predicted actor values do not corrupt the server-confirmed state.
      outState[propName] = prevState[propName];
      continue;
    }

    const dotPath = pathPrefix ? `${pathPrefix}.${propName}` : propName;
    const currentValue = (obj as Record<string, unknown>)[propName];
    const prevCaptured = prevState[propName];

    const { decoded, captured } = decodeValueFromBinary(
      reader, currentValue, prevCaptured, net, resolveActorRef, dotPath, unresolvedRefs, pendingCallbacks,
    );

    outState[propName] = captured;

    if (decoded !== UNRESOLVED_SENTINEL) {
      (obj as Record<string, unknown>)[propName] = decoded;
      collectRepCallback(obj, metadata, propName, pendingCallbacks);
    }
  }
}

function decodeValueFromBinary(
  reader: BinaryReader,
  currentValue: unknown,
  prevCaptured: unknown,
  net: NetPropertyMetadata,
  resolveActorRef: ((uuid: string) => Actor | null) | undefined,
  dotPath: string,
  unresolvedRefs: Map<string, string>,
  pendingCallbacks: Array<[object, string]>,
): { decoded: unknown; captured: unknown } {
  if (net.nullable) {
    if (!reader.readPresence()) return { decoded: null, captured: null };
  }

  switch (net.netType) {
    case 'bool': { const v = reader.readBool(); return { decoded: v, captured: v }; }
    case 'uint':
    case 'int': {
      const delta = reader.readVarsint();
      const v = (typeof prevCaptured === 'number' ? prevCaptured : 0) + delta;
      return { decoded: v, captured: v };
    }
    case 'float': {
      if (net.quantization) {
        const q = net.quantization;
        if (q.mode === 'float64') {
          const v = reader.readFloat64();
          return { decoded: v, captured: v };
        }
        // scale, range, float16, float32 — all use varsint delta on quantized integer.
        const prevQi = typeof prevCaptured === 'number' ? prevCaptured : quantizeScalar(0, q);
        const qi = prevQi + reader.readVarsint();
        return { decoded: dequantizeScalar(qi, q), captured: qi };
      }
      const v = reader.readFloat32();
      return { decoded: v, captured: v };
    }
    case 'string': { const v = reader.readString(); return { decoded: v, captured: v }; }

    case 'enum': {
      const opts = net.enumOptions ?? [];
      const idx = reader.readBits(bitsForEnum(opts));
      const v = opts[idx] ?? opts[0] ?? '';
      return { decoded: v, captured: v };
    }

    case 'actorRef': {
      const uuid = reader.readActorId();
      if (!uuid) return { decoded: null, captured: null };
      // Always capture the UUID from the wire — even when unresolved.
      const captured: ActorRef = { $actorRef: uuid };
      if (resolveActorRef) {
        const resolved = resolveActorRef(uuid);
        if (resolved) return { decoded: resolved, captured };
      }
      unresolvedRefs.set(dotPath, uuid);
      return { decoded: UNRESOLVED_SENTINEL, captured };
    }

    case 'vector2': {
      const mask = reader.readBits(2);
      const prevArr = Array.isArray(prevCaptured) ? prevCaptured as number[] : null;
      if (net.quantization) {
        const q = net.quantization;
        const qx = (mask & 1) ? ((prevArr?.[0] ?? 0) + reader.readVarsint()) : (prevArr?.[0] ?? 0);
        const qy = (mask & 2) ? ((prevArr?.[1] ?? 0) + reader.readVarsint()) : (prevArr?.[1] ?? 0);
        const x = dequantizeScalar(qx, q), y = dequantizeScalar(qy, q);
        if (currentValue != null) {
          const cv = currentValue as Record<string, unknown>;
          cv.x = x; cv.y = y;
          return { decoded: currentValue, captured: [qx, qy] };
        }
        return { decoded: { x, y }, captured: [qx, qy] };
      }
      const x = (mask & 1) ? readVecComponentDelta(reader, prevArr?.[0] ?? 0, net.vectorElementType) : (prevArr?.[0] ?? 0);
      const y = (mask & 2) ? readVecComponentDelta(reader, prevArr?.[1] ?? 0, net.vectorElementType) : (prevArr?.[1] ?? 0);
      const captured = [x, y];
      if (currentValue != null) {
        const cv = currentValue as Record<string, unknown>;
        cv.x = x; cv.y = y;
        return { decoded: currentValue, captured };
      }
      return { decoded: { x, y }, captured };
    }

    case 'vector3':
    case 'euler': {
      const mask = reader.readBits(3);
      const prevArr = Array.isArray(prevCaptured) ? prevCaptured as number[] : null;
      if (net.quantization) {
        const q = net.quantization;
        const qx = (mask & 1) ? ((prevArr?.[0] ?? 0) + reader.readVarsint()) : (prevArr?.[0] ?? 0);
        const qy = (mask & 2) ? ((prevArr?.[1] ?? 0) + reader.readVarsint()) : (prevArr?.[1] ?? 0);
        const qz = (mask & 4) ? ((prevArr?.[2] ?? 0) + reader.readVarsint()) : (prevArr?.[2] ?? 0);
        const x = dequantizeScalar(qx, q), y = dequantizeScalar(qy, q), z = dequantizeScalar(qz, q);
        if (currentValue != null) {
          const cv = currentValue as Record<string, unknown>;
          cv.x = x; cv.y = y; cv.z = z;
          return { decoded: currentValue, captured: [qx, qy, qz] };
        }
        return { decoded: { x, y, z }, captured: [qx, qy, qz] };
      }
      const x = (mask & 1) ? readVecComponentDelta(reader, prevArr?.[0] ?? 0, net.vectorElementType) : (prevArr?.[0] ?? 0);
      const y = (mask & 2) ? readVecComponentDelta(reader, prevArr?.[1] ?? 0, net.vectorElementType) : (prevArr?.[1] ?? 0);
      const z = (mask & 4) ? readVecComponentDelta(reader, prevArr?.[2] ?? 0, net.vectorElementType) : (prevArr?.[2] ?? 0);
      const captured = [x, y, z];
      if (currentValue != null) {
        const cv = currentValue as Record<string, unknown>;
        cv.x = x; cv.y = y; cv.z = z;
        return { decoded: currentValue, captured };
      }
      return { decoded: { x, y, z }, captured };
    }

    case 'vector4':
    case 'quaternion': {
      const prevArr = Array.isArray(prevCaptured) ? prevCaptured as number[] : null;
      if (net.quantization?.mode === 'drop-w') {
        return decodeDropW(reader, currentValue, prevArr, net.quantization.bits);
      }
      const mask = reader.readBits(4);
      if (net.quantization) {
        const q = net.quantization;
        const qx = (mask & 1) ? ((prevArr?.[0] ?? 0) + reader.readVarsint()) : (prevArr?.[0] ?? 0);
        const qy = (mask & 2) ? ((prevArr?.[1] ?? 0) + reader.readVarsint()) : (prevArr?.[1] ?? 0);
        const qz = (mask & 4) ? ((prevArr?.[2] ?? 0) + reader.readVarsint()) : (prevArr?.[2] ?? 0);
        const qw = (mask & 8) ? ((prevArr?.[3] ?? 0) + reader.readVarsint()) : (prevArr?.[3] ?? 0);
        const x = dequantizeScalar(qx, q), y = dequantizeScalar(qy, q);
        const z = dequantizeScalar(qz, q), w = dequantizeScalar(qw, q);
        if (currentValue != null) {
          const cv = currentValue as Record<string, unknown>;
          cv.x = x; cv.y = y; cv.z = z; cv.w = w;
          return { decoded: currentValue, captured: [qx, qy, qz, qw] };
        }
        return { decoded: { x, y, z, w }, captured: [qx, qy, qz, qw] };
      }
      const x = (mask & 1) ? readVecComponentDelta(reader, prevArr?.[0] ?? 0, net.vectorElementType) : (prevArr?.[0] ?? 0);
      const y = (mask & 2) ? readVecComponentDelta(reader, prevArr?.[1] ?? 0, net.vectorElementType) : (prevArr?.[1] ?? 0);
      const z = (mask & 4) ? readVecComponentDelta(reader, prevArr?.[2] ?? 0, net.vectorElementType) : (prevArr?.[2] ?? 0);
      const w = (mask & 8) ? readVecComponentDelta(reader, prevArr?.[3] ?? 0, net.vectorElementType) : (prevArr?.[3] ?? 0);
      const captured = [x, y, z, w];
      if (currentValue != null) {
        const cv = currentValue as Record<string, unknown>;
        cv.x = x; cv.y = y; cv.z = z; cv.w = w;
        return { decoded: currentValue, captured };
      }
      return { decoded: { x, y, z, w }, captured };
    }

    case 'array': {
      const isDelta = reader.readBit();
      const existingArr = Array.isArray(currentValue) ? currentValue : [];
      const prevArr = Array.isArray(prevCaptured) ? prevCaptured : [];

      if (isDelta) {
        const count = existingArr.length;
        const elemPresent = reader.readBitmask(count);
        // Snapshot starts from the previous confirmed array; only changed elements are overwritten.
        const capturedArr = [...prevArr];
        capturedArr.length = count;
        const resultArr = [...existingArr];

        for (let j = 0; j < count; j++) {
          if (!elemPresent[j]) continue;
          if (net.element) {
            const { decoded: ed, captured: ec } = decodeValueFromBinary(
              reader, existingArr[j] ?? null, prevArr[j] ?? null, net.element,
              resolveActorRef, `${dotPath}.[${j}]`, unresolvedRefs, pendingCallbacks,
            );
            capturedArr[j] = ec;
            resultArr[j] = ed === UNRESOLVED_SENTINEL ? null : ed;
          }
        }
        return { decoded: resultArr, captured: capturedArr };
      }

      // Full array (length changed or forced).
      const fullCount = reader.readVaruint();
      const capturedArr: unknown[] = [];
      const resultArr: unknown[] = [];
      for (let j = 0; j < fullCount; j++) {
        if (net.element) {
          const { decoded: ed, captured: ec } = decodeValueFromBinary(
            reader, existingArr[j] ?? null, prevArr[j] ?? null, net.element,
            resolveActorRef, `${dotPath}.[${j}]`, unresolvedRefs, pendingCallbacks,
          );
          capturedArr.push(ec);
          resultArr.push(ed === UNRESOLVED_SENTINEL ? null : ed);
        }
      }
      return { decoded: resultArr, captured: capturedArr };
    }

    case 'struct': {
      let instance = currentValue;
      if ((instance === null || instance === undefined) && net.structClass) {
        instance = new (net.structClass as new () => object)();
      }
      if (instance === null || instance === undefined) {
        throw new Error(
          `[ObjectSerializer] Cannot decode struct for property "${dotPath}": ` +
          'no current instance and no structClass constructor in net metadata.',
        );
      }
      const prevNested = (prevCaptured !== null && prevCaptured !== undefined
        && typeof prevCaptured === 'object' && !Array.isArray(prevCaptured))
        ? prevCaptured as Record<string, unknown>
        : {};
      const nestedOut: Record<string, unknown> = {};
      decodeObjectFromBinary(
        reader, instance as object, prevNested, nestedOut, resolveActorRef, dotPath, unresolvedRefs, pendingCallbacks,
      );
      return { decoded: instance, captured: nestedOut };
    }

    case 'class': {
      let ctor: (new (...args: any[]) => any) | null = null;
      if (net.polymorphic) {
        ctor = readClassDiscriminator(reader);
        if (!ctor) {
          throw new Error(
            `[ObjectSerializer] Unknown polymorphic class hash for property "${dotPath}". ` +
            'Packet is corrupt or the class was removed from ClassRegistry.',
          );
        }
      } else if (net.netBaseClass) {
        try {
          ctor = ClassRegistry.getClass(net.netBaseClass);
        } catch {
          throw new Error(
            `[ObjectSerializer] Class "${net.netBaseClass}" not found in ClassRegistry for property "${dotPath}". ` +
            'Ensure the class is registered before receiving data.',
          );
        }
      }

      let instance = currentValue;
      if (ctor && (instance === null || instance === undefined || !(instance instanceof ctor))) {
        instance = new ctor();
      }
      if (instance === null || instance === undefined) {
        throw new Error(
          `[ObjectSerializer] Cannot decode class for property "${dotPath}": ` +
          'no constructor resolved.',
        );
      }
      const prevNested = (prevCaptured !== null && prevCaptured !== undefined
        && typeof prevCaptured === 'object' && !Array.isArray(prevCaptured))
        ? prevCaptured as Record<string, unknown>
        : {};
      const nestedOut: Record<string, unknown> = {};
      decodeObjectFromBinary(
        reader, instance as object, prevNested, nestedOut, resolveActorRef, dotPath, unresolvedRefs, pendingCallbacks,
      );
      return { decoded: instance, captured: nestedOut };
    }

    default:
      console.warn(`[ObjectSerializer] Unknown netType in decode: ${(net as NetPropertyMetadata).netType}`);
      return { decoded: currentValue, captured: currentValue };
  }
}

// ---------------------------------------------------------------------------
// Private: combined encode
// ---------------------------------------------------------------------------

function encodeValueCombined(
  writer: BinaryWriter,
  value: unknown,
  lastSer: unknown,
  net: NetPropertyMetadata,
): { serialized: unknown; changed: boolean } {
  if (net.nullable && (value === null || value === undefined)) {
    // null → null: no change. undefined lastSer (first tick) or non-null lastSer: send null marker.
    const changed = lastSer !== null;
    if (changed) writer.writePresence(false);
    return { serialized: null, changed };
  }

  switch (net.netType) {
    case 'bool': {
      const v = value as boolean;
      const changed = v !== lastSer;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeBool(v);
      }
      return { serialized: v, changed };
    }

    case 'uint':
    case 'int': {
      const v = typeof value === 'number' ? value : 0;
      const prev = typeof lastSer === 'number' ? lastSer : 0;
      const eps = net.epsilon ?? 0;
      const changed = eps > 0 ? Math.abs(v - prev) > eps : v !== prev;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeVarsint(v - prev);
      }
      return { serialized: v, changed };
    }

    case 'float': {
      const v = typeof value === 'number' ? value : 0;
      const eps = net.epsilon ?? 0;
      if (net.quantization) {
        const q = net.quantization;
        if (q.mode === 'float64') {
          // float64 bit patterns exceed 32-bit integer space — send absolute value.
          const prev = typeof lastSer === 'number' ? lastSer : 0;
          const changed = eps > 0 ? Math.abs(v - prev) > eps : v !== prev;
          if (changed) {
            if (net.nullable) writer.writePresence(true);
            writer.writeFloat64(v);
          }
          return { serialized: v, changed };
        }
        // scale, range, float16, float32 — all go through quantizeScalar.
        const qi = quantizeScalar(v, q);
        const prevQi = typeof lastSer === 'number' ? lastSer : quantizeScalar(0, q);
        const prevFloat = dequantizeScalar(prevQi, q);
        const changed = eps > 0 ? Math.abs(v - prevFloat) > eps : qi !== prevQi;
        if (changed) {
          if (net.nullable) writer.writePresence(true);
          writer.writeVarsint(qi - prevQi);
        }
        return { serialized: qi, changed };
      }
      // No quantization — absolute float32.
      const prev = typeof lastSer === 'number' ? lastSer : 0;
      const changed = eps > 0 ? Math.abs(v - prev) > eps : v !== prev;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeFloat32(v);
      }
      return { serialized: v, changed };
    }

    case 'string': {
      const v = typeof value === 'string' ? value : '';
      const changed = v !== lastSer;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeString(v);
      }
      return { serialized: v, changed };
    }

    case 'enum': {
      const changed = value !== lastSer;
      if (changed) {
        const opts = net.enumOptions ?? [];
        const idx = opts.indexOf(value as string);
        if (net.nullable) writer.writePresence(true);
        writer.writeBits(idx >= 0 ? idx : 0, bitsForEnum(opts));
      }
      return { serialized: value, changed };
    }

    case 'actorRef': {
      const uuid = value instanceof Actor ? value.uuid : (value as ActorRef)?.$actorRef ?? '';
      const ser: ActorRef = { $actorRef: uuid };
      const prevUuid = isActorRef(lastSer) ? lastSer.$actorRef : undefined;
      const changed = prevUuid !== uuid;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeActorId(uuid);
      }
      return { serialized: ser, changed };
    }

    case 'vector2': {
      const v = value as { x: number; y: number };
      const lastArr = Array.isArray(lastSer) ? lastSer as number[] : null;
      const eps = net.epsilon ?? 0;
      if (net.quantization) {
        const q = net.quantization;
        const qx = quantizeScalar(v.x, q), qy = quantizeScalar(v.y, q);
        const xC = !lastArr || (eps > 0 ? Math.abs(qx - lastArr[0]) > eps : lastArr[0] !== qx);
        const yC = !lastArr || (eps > 0 ? Math.abs(qy - lastArr[1]) > eps : lastArr[1] !== qy);
        const changed = xC || yC;
        if (changed) {
          if (net.nullable) writer.writePresence(true);
          writer.writeBits((xC ? 1 : 0) | (yC ? 2 : 0), 2);
          if (xC) writer.writeVarsint(qx - (lastArr?.[0] ?? 0));
          if (yC) writer.writeVarsint(qy - (lastArr?.[1] ?? 0));
        }
        return { serialized: [qx, qy], changed };
      }
      const serialized = [v.x, v.y];
      const xC = !lastArr || (eps > 0 ? Math.abs(v.x - lastArr[0]) > eps : lastArr[0] !== v.x);
      const yC = !lastArr || (eps > 0 ? Math.abs(v.y - lastArr[1]) > eps : lastArr[1] !== v.y);
      const changed = xC || yC;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeBits((xC ? 1 : 0) | (yC ? 2 : 0), 2);
        if (xC) writeVecComponentDelta(writer, v.x, lastArr?.[0] ?? 0, net.vectorElementType);
        if (yC) writeVecComponentDelta(writer, v.y, lastArr?.[1] ?? 0, net.vectorElementType);
      }
      return { serialized, changed };
    }

    case 'vector3':
    case 'euler': {
      const v = value as { x: number; y: number; z: number };
      const lastArr = Array.isArray(lastSer) ? lastSer as number[] : null;
      const eps = net.epsilon ?? 0;
      if (net.quantization) {
        const q = net.quantization;
        const qx = quantizeScalar(v.x, q), qy = quantizeScalar(v.y, q), qz = quantizeScalar(v.z, q);
        const xC = !lastArr || (eps > 0 ? Math.abs(qx - lastArr[0]) > eps : lastArr[0] !== qx);
        const yC = !lastArr || (eps > 0 ? Math.abs(qy - lastArr[1]) > eps : lastArr[1] !== qy);
        const zC = !lastArr || (eps > 0 ? Math.abs(qz - lastArr[2]) > eps : lastArr[2] !== qz);
        const changed = xC || yC || zC;
        if (changed) {
          if (net.nullable) writer.writePresence(true);
          writer.writeBits((xC ? 1 : 0) | (yC ? 2 : 0) | (zC ? 4 : 0), 3);
          if (xC) writer.writeVarsint(qx - (lastArr?.[0] ?? 0));
          if (yC) writer.writeVarsint(qy - (lastArr?.[1] ?? 0));
          if (zC) writer.writeVarsint(qz - (lastArr?.[2] ?? 0));
        }
        return { serialized: [qx, qy, qz], changed };
      }
      const serialized = [v.x, v.y, v.z];
      const xC = !lastArr || (eps > 0 ? Math.abs(v.x - lastArr[0]) > eps : lastArr[0] !== v.x);
      const yC = !lastArr || (eps > 0 ? Math.abs(v.y - lastArr[1]) > eps : lastArr[1] !== v.y);
      const zC = !lastArr || (eps > 0 ? Math.abs(v.z - lastArr[2]) > eps : lastArr[2] !== v.z);
      const changed = xC || yC || zC;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeBits((xC ? 1 : 0) | (yC ? 2 : 0) | (zC ? 4 : 0), 3);
        if (xC) writeVecComponentDelta(writer, v.x, lastArr?.[0] ?? 0, net.vectorElementType);
        if (yC) writeVecComponentDelta(writer, v.y, lastArr?.[1] ?? 0, net.vectorElementType);
        if (zC) writeVecComponentDelta(writer, v.z, lastArr?.[2] ?? 0, net.vectorElementType);
      }
      return { serialized, changed };
    }

    case 'vector4':
    case 'quaternion': {
      const v = value as { x: number; y: number; z: number; w: number };
      const lastArr = Array.isArray(lastSer) ? lastSer as number[] : null;
      const eps = net.epsilon ?? 0;
      if (net.quantization) {
        const q = net.quantization;
        if (q.mode === 'drop-w') {
          const { serialized, changed } = encodeDropW(writer, v, q.bits, lastArr, eps, net.nullable ?? false);
          return { serialized, changed };
        }
        const qx = quantizeScalar(v.x, q), qy = quantizeScalar(v.y, q);
        const qz = quantizeScalar(v.z, q), qw = quantizeScalar(v.w, q);
        const xC = !lastArr || (eps > 0 ? Math.abs(qx - lastArr[0]) > eps : lastArr[0] !== qx);
        const yC = !lastArr || (eps > 0 ? Math.abs(qy - lastArr[1]) > eps : lastArr[1] !== qy);
        const zC = !lastArr || (eps > 0 ? Math.abs(qz - lastArr[2]) > eps : lastArr[2] !== qz);
        const wC = !lastArr || (eps > 0 ? Math.abs(qw - lastArr[3]) > eps : lastArr[3] !== qw);
        const changed = xC || yC || zC || wC;
        if (changed) {
          if (net.nullable) writer.writePresence(true);
          writer.writeBits((xC ? 1 : 0) | (yC ? 2 : 0) | (zC ? 4 : 0) | (wC ? 8 : 0), 4);
          if (xC) writer.writeVarsint(qx - (lastArr?.[0] ?? 0));
          if (yC) writer.writeVarsint(qy - (lastArr?.[1] ?? 0));
          if (zC) writer.writeVarsint(qz - (lastArr?.[2] ?? 0));
          if (wC) writer.writeVarsint(qw - (lastArr?.[3] ?? 0));
        }
        return { serialized: [qx, qy, qz, qw], changed };
      }
      const serialized = [v.x, v.y, v.z, v.w];
      const xC = !lastArr || (eps > 0 ? Math.abs(v.x - lastArr[0]) > eps : lastArr[0] !== v.x);
      const yC = !lastArr || (eps > 0 ? Math.abs(v.y - lastArr[1]) > eps : lastArr[1] !== v.y);
      const zC = !lastArr || (eps > 0 ? Math.abs(v.z - lastArr[2]) > eps : lastArr[2] !== v.z);
      const wC = !lastArr || (eps > 0 ? Math.abs(v.w - lastArr[3]) > eps : lastArr[3] !== v.w);
      const changed = xC || yC || zC || wC;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeBits((xC ? 1 : 0) | (yC ? 2 : 0) | (zC ? 4 : 0) | (wC ? 8 : 0), 4);
        if (xC) writeVecComponentDelta(writer, v.x, lastArr?.[0] ?? 0, net.vectorElementType);
        if (yC) writeVecComponentDelta(writer, v.y, lastArr?.[1] ?? 0, net.vectorElementType);
        if (zC) writeVecComponentDelta(writer, v.z, lastArr?.[2] ?? 0, net.vectorElementType);
        if (wC) writeVecComponentDelta(writer, v.w, lastArr?.[3] ?? 0, net.vectorElementType);
      }
      return { serialized, changed };
    }

    case 'struct':
    case 'class': {
      if (value === null || value === undefined) {
        // undefined lastSer (first tick) or non-null lastSer: send null marker.
        const changed = lastSer !== null;
        if (changed) {
          if (net.nullable) {
            writer.writePresence(false);
          } else if (net.netType === 'class' && net.polymorphic) {
            writer.writeUint32(0);
          } else {
            const meta = net.structClass ? getClassObjectMetadata(net.structClass) : null;
            if (meta) writer.reserveBitmask(meta.replicatedProps.length);
          }
        }
        return { serialized: null, changed };
      }

      const lastNested = (lastSer !== null && lastSer !== undefined
        && typeof lastSer === 'object' && !Array.isArray(lastSer))
        ? lastSer as Record<string, unknown>
        : null;

      const nestedState: Record<string, unknown> = {};
      const beforeOffset = writer.bitCursor;

      if (net.nullable) writer.writePresence(true);
      if (net.netType === 'class' && net.polymorphic) writeClassDiscriminator(writer, value as object);

      const nestedChanged = serializeObjectAndEncode(
        writer, value as object, lastNested ?? {}, nestedState,
      );

      if (!nestedChanged) {
        writer.resetToBit(beforeOffset);
        return { serialized: nestedState, changed: false };
      }
      return { serialized: nestedState, changed: true };
    }

    case 'array': {
      const arr = Array.isArray(value) ? value : [];
      const lastArr = Array.isArray(lastSer) ? lastSer as unknown[] : null;
      const lengthChanged = lastArr === null || arr.length !== lastArr.length;
      const canDelta = !lengthChanged;

      const serialized: unknown[] = new Array(arr.length);

      if (canDelta) {
        const beforeArray = writer.bitCursor;
        if (net.nullable) writer.writePresence(true);
        writer.writeBit(true); // delta mode flag
        const bitmaskCursor = writer.reserveBitmask(arr.length);
        const changedIndices = new Set<number>();

        for (let i = 0; i < arr.length; i++) {
          const elemBefore = writer.bitCursor;
          const { serialized: elemSer, changed: elemChanged } =
            net.element
              ? encodeValueCombined(writer, arr[i], lastArr![i], net.element)
              : { serialized: arr[i], changed: false };
          serialized[i] = elemSer;
          if (elemChanged) {
            changedIndices.add(i);
          } else {
            writer.resetToBit(elemBefore);
          }
        }

        if (changedIndices.size === 0) {
          writer.resetToBit(beforeArray);
          return { serialized, changed: false };
        }

        writer.patchBitmask(bitmaskCursor, arr.length, changedIndices);
        return { serialized, changed: true };
      }

      let anyChanged = lengthChanged;
      if (net.nullable) writer.writePresence(true);
      writer.writeBit(false); // full mode flag
      writer.writeVaruint(arr.length);
      for (let i = 0; i < arr.length; i++) {
        const { serialized: elemSer, changed: elemChanged } =
          net.element
            ? encodeValueCombined(writer, arr[i], lastArr?.[i], net.element)
            : { serialized: arr[i], changed: false };
        serialized[i] = elemSer;
        if (elemChanged) anyChanged = true;
      }
      return { serialized, changed: anyChanged };
    }

    default:
      console.warn(`[ObjectSerializer] Unknown netType: ${(net as NetPropertyMetadata).netType}`);
      return { serialized: value, changed: false };
  }
}

// ---------------------------------------------------------------------------
// RPC object serialization (no bitmask, no state capture, actorRefs as UUIDs)
// ---------------------------------------------------------------------------

/**
 * Serialize all replicated properties of `obj` into `writer` without a bitmask
 * or state snapshot. Used for `struct`/`class` RPC parameters where all fields
 * are always present and no delta tracking is needed.
 */
export function serializeObjectForRpc(writer: BinaryWriter, obj: object): void {
  const { replicatedProps, netMeta } = getClassObjectMetadata(obj.constructor);
  for (const propName of replicatedProps) {
    const net = netMeta.get(propName);
    if (!net) continue;
    encodeRpcValue(writer, (obj as Record<string, unknown>)[propName], net);
  }
}

/**
 * Decode replicated properties from `reader` into `instance` without a bitmask,
 * dangling-ref tracking, or `onRep*` callbacks. Actor references decode as UUID
 * strings. Used for `struct`/`class` RPC parameters.
 */
export function decodeObjectForRpc(reader: BinaryReader, instance: object): void {
  const { replicatedProps, netMeta } = getClassObjectMetadata(instance.constructor);
  for (const propName of replicatedProps) {
    const net = netMeta.get(propName);
    if (!net) continue;
    (instance as Record<string, unknown>)[propName] = decodeRpcValue(
      reader, (instance as Record<string, unknown>)[propName], net,
    );
  }
}

export function encodeRpcValue(writer: BinaryWriter, value: unknown, net: NetPropertyMetadata): void {
  if (net.nullable && (value === null || value === undefined)) {
    writer.writePresence(false);
    return;
  }

  switch (net.netType) {
    case 'bool':
      if (net.nullable) writer.writePresence(true);
      writer.writeBool(value as boolean);
      break;

    case 'uint':
      if (net.nullable) writer.writePresence(true);
      writer.writeVaruint(typeof value === 'number' ? value : 0);
      break;

    case 'int':
      if (net.nullable) writer.writePresence(true);
      writer.writeVarsint(typeof value === 'number' ? value : 0);
      break;

    case 'float': {
      const v = typeof value === 'number' ? value : 0;
      if (net.nullable) writer.writePresence(true);
      if (net.quantization) {
        const q = net.quantization;
        if (q.mode === 'float16') { writer.writeFloat16(v); break; }
        if (q.mode === 'float32') { writer.writeFloat32(v); break; }
        if (q.mode === 'float64') { writer.writeFloat64(v); break; }
        writeQuantizedScalar(writer, quantizeScalar(v, q), q);
      } else {
        writer.writeFloat32(v);
      }
      break;
    }

    case 'string':
      if (net.nullable) writer.writePresence(true);
      writer.writeString(typeof value === 'string' ? value : '');
      break;

    case 'enum': {
      const opts = net.enumOptions ?? [];
      const idx = opts.indexOf(value as string);
      if (net.nullable) writer.writePresence(true);
      writer.writeBits(idx >= 0 ? idx : 0, bitsForEnum(opts));
      break;
    }

    case 'actorRef': {
      const uuid = value instanceof Actor ? value.uuid : (isActorRef(value) ? value.$actorRef : '');
      if (net.nullable) writer.writePresence(true);
      writer.writeActorId(uuid);
      break;
    }

    case 'vector2': {
      const v = value as { x: number; y: number };
      if (net.nullable) writer.writePresence(true);
      if (net.quantization) {
        const q = net.quantization;
        writeQuantizedScalar(writer, quantizeScalar(v.x, q), q);
        writeQuantizedScalar(writer, quantizeScalar(v.y, q), q);
      } else {
        writeVecComponent(writer, v.x, net.vectorElementType);
        writeVecComponent(writer, v.y, net.vectorElementType);
      }
      break;
    }

    case 'vector3':
    case 'euler': {
      const v = value as { x: number; y: number; z: number };
      if (net.nullable) writer.writePresence(true);
      if (net.quantization) {
        const q = net.quantization;
        writeQuantizedScalar(writer, quantizeScalar(v.x, q), q);
        writeQuantizedScalar(writer, quantizeScalar(v.y, q), q);
        writeQuantizedScalar(writer, quantizeScalar(v.z, q), q);
      } else {
        writeVecComponent(writer, v.x, net.vectorElementType);
        writeVecComponent(writer, v.y, net.vectorElementType);
        writeVecComponent(writer, v.z, net.vectorElementType);
      }
      break;
    }

    case 'vector4':
    case 'quaternion': {
      const v = value as { x: number; y: number; z: number; w: number };
      if (net.nullable) writer.writePresence(true);
      if (net.quantization) {
        const q = net.quantization;
        if (q.mode === 'drop-w') {
          encodeRpcDropW(writer, v, q.bits);
        } else {
          writeQuantizedScalar(writer, quantizeScalar(v.x, q), q);
          writeQuantizedScalar(writer, quantizeScalar(v.y, q), q);
          writeQuantizedScalar(writer, quantizeScalar(v.z, q), q);
          writeQuantizedScalar(writer, quantizeScalar(v.w, q), q);
        }
      } else {
        writeVecComponent(writer, v.x, net.vectorElementType);
        writeVecComponent(writer, v.y, net.vectorElementType);
        writeVecComponent(writer, v.z, net.vectorElementType);
        writeVecComponent(writer, v.w, net.vectorElementType);
      }
      break;
    }

    case 'struct':
    case 'class': {
      if (value === null || value === undefined) {
        if (net.nullable) {
          writer.writePresence(false);
        } else if (net.netType === 'class' && net.polymorphic) {
          writer.writeUint32(0);
        }
        return;
      }
      if (net.nullable) writer.writePresence(true);
      if (net.netType === 'class' && net.polymorphic) writeClassDiscriminator(writer, value as object);
      serializeObjectForRpc(writer, value as object);
      break;
    }

    case 'array': {
      const arr = Array.isArray(value) ? value : [];
      if (net.nullable) writer.writePresence(true);
      writer.writeVaruint(arr.length);
      if (net.element) {
        for (const elem of arr) encodeRpcValue(writer, elem, net.element);
      }
      break;
    }

    default:
      console.warn(`[ObjectSerializer] Unknown netType in RPC encode: ${(net as NetPropertyMetadata).netType}`);
  }
}

export function decodeRpcValue(reader: BinaryReader, currentValue: unknown, net: NetPropertyMetadata): unknown {
  if (net.nullable && !reader.readPresence()) return null;

  switch (net.netType) {
    case 'bool': return reader.readBool();
    case 'uint': return reader.readVaruint();
    case 'int': return reader.readVarsint();
    case 'float': {
      if (net.quantization) {
        const q = net.quantization;
        if (q.mode === 'float16') return reader.readFloat16();
        if (q.mode === 'float32') return reader.readFloat32();
        if (q.mode === 'float64') return reader.readFloat64();
        return dequantizeScalar(readQuantizedScalar(reader, q), q);
      }
      return reader.readFloat32();
    }
    case 'string': return reader.readString();

    case 'enum': {
      const opts = net.enumOptions ?? [];
      const idx = reader.readBits(bitsForEnum(opts));
      return opts[idx] ?? opts[0] ?? '';
    }

    case 'actorRef': {
      const uuid = reader.readActorId();
      return uuid || null;
    }

    case 'vector2': {
      let x: number, y: number;
      if (net.quantization) {
        const q = net.quantization;
        x = dequantizeScalar(readQuantizedScalar(reader, q), q);
        y = dequantizeScalar(readQuantizedScalar(reader, q), q);
      } else {
        x = readVecComponent(reader, net.vectorElementType);
        y = readVecComponent(reader, net.vectorElementType);
      }
      if (currentValue != null) {
        const cv = currentValue as Record<string, unknown>;
        cv.x = x; cv.y = y;
        return currentValue;
      }
      return { x, y };
    }

    case 'vector3':
    case 'euler': {
      let x: number, y: number, z: number;
      if (net.quantization) {
        const q = net.quantization;
        x = dequantizeScalar(readQuantizedScalar(reader, q), q);
        y = dequantizeScalar(readQuantizedScalar(reader, q), q);
        z = dequantizeScalar(readQuantizedScalar(reader, q), q);
      } else {
        x = readVecComponent(reader, net.vectorElementType);
        y = readVecComponent(reader, net.vectorElementType);
        z = readVecComponent(reader, net.vectorElementType);
      }
      if (currentValue != null) {
        const cv = currentValue as Record<string, unknown>;
        cv.x = x; cv.y = y; cv.z = z;
        return currentValue;
      }
      return { x, y, z };
    }

    case 'vector4':
    case 'quaternion': {
      let x: number, y: number, z: number, w: number;
      if (net.quantization) {
        const q = net.quantization;
        if (q.mode === 'drop-w') {
          return decodeRpcDropW(reader, q.bits, currentValue);
        }
        x = dequantizeScalar(readQuantizedScalar(reader, q), q);
        y = dequantizeScalar(readQuantizedScalar(reader, q), q);
        z = dequantizeScalar(readQuantizedScalar(reader, q), q);
        w = dequantizeScalar(readQuantizedScalar(reader, q), q);
      } else {
        x = readVecComponent(reader, net.vectorElementType);
        y = readVecComponent(reader, net.vectorElementType);
        z = readVecComponent(reader, net.vectorElementType);
        w = readVecComponent(reader, net.vectorElementType);
      }
      if (currentValue != null) {
        const cv = currentValue as Record<string, unknown>;
        cv.x = x; cv.y = y; cv.z = z; cv.w = w;
        return currentValue;
      }
      return { x, y, z, w };
    }

    case 'struct': {
      let instance = currentValue;
      if ((instance === null || instance === undefined) && net.structClass) {
        instance = new (net.structClass as new () => object)();
      }
      if (instance === null || instance === undefined) {
        throw new Error('[ObjectSerializer] Cannot decode RPC struct param: no structClass in net metadata.');
      }
      decodeObjectForRpc(reader, instance as object);
      return instance;
    }

    case 'class': {
      let ctor: (new (...args: any[]) => any) | null = null;
      if (net.polymorphic) {
        ctor = readClassDiscriminator(reader);
      }
      let instance = currentValue;
      if (ctor && (instance === null || instance === undefined || !(instance instanceof ctor))) {
        instance = new ctor();
      }
      if (instance === null || instance === undefined) {
        throw new Error('[ObjectSerializer] Cannot decode RPC class param: no constructor resolved.');
      }
      decodeObjectForRpc(reader, instance as object);
      return instance;
    }

    case 'array': {
      const count = reader.readVaruint();
      const result: unknown[] = [];
      const existingArr = Array.isArray(currentValue) ? currentValue : [];
      if (net.element) {
        for (let i = 0; i < count; i++) {
          result.push(decodeRpcValue(reader, existingArr[i] ?? null, net.element));
        }
      }
      return result;
    }

    default:
      console.warn(`[ObjectSerializer] Unknown netType in RPC decode: ${(net as NetPropertyMetadata).netType}`);
      return currentValue;
  }
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Returns the number of bits needed to represent an enum index.
 * At least 1 bit; `ceil(log2(N))` for N ≥ 2 options.
 * Must be called with the same `opts` on both encode and decode sides.
 */
function bitsForEnum(opts: string[]): number {
  return Math.max(1, Math.ceil(Math.log2(Math.max(2, opts.length))));
}

function collectRepCallback(
  obj: object,
  metadata: ClassObjectMetadata,
  propName: string,
  pendingCallbacks: Array<[object, string]>,
): void {
  const callbackName = metadata.repCallbacks.get(propName);
  if (callbackName && typeof (obj as Record<string, unknown>)[callbackName] === 'function') {
    pendingCallbacks.push([obj, callbackName]);
  }
}

// ---------------------------------------------------------------------------
// Vector component read/write helpers
// ---------------------------------------------------------------------------

type VecElementType = NetPropertyMetadata['vectorElementType'];

function writeVecComponent(writer: BinaryWriter, v: number, type: VecElementType): void {
  switch (type) {
    case 'int': writer.writeVarsint(v); break;
    case 'uint': writer.writeVaruint(v); break;
    default: writer.writeFloat32(v); break;
  }
}

function readVecComponent(reader: BinaryReader, type: VecElementType): number {
  switch (type) {
    case 'int': return reader.readVarsint();
    case 'uint': return reader.readVaruint();
    default: return reader.readFloat32();
  }
}

/**
 * Write a vector component as a delta (`newVal - prevVal`).
 * Int/uint types use a varsint delta; float components are written as absolute float32 values.
 */
function writeVecComponentDelta(writer: BinaryWriter, newVal: number, prevVal: number, type: VecElementType): void {
  switch (type) {
    case 'int':
    case 'uint':
      writer.writeVarsint(newVal - prevVal);
      break;
    default:
      writer.writeFloat32(newVal);
      break;
  }
}

/**
 * Read a vector component written by {@link writeVecComponentDelta}.
 * Int/uint types reconstruct the absolute value from `prevVal + delta`;
 * float components are read as absolute float32 values.
 */
function readVecComponentDelta(reader: BinaryReader, prevVal: number, type: VecElementType): number {
  switch (type) {
    case 'int':
    case 'uint':
      return prevVal + reader.readVarsint();
    default:
      return reader.readFloat32();
  }
}

// ---------------------------------------------------------------------------
// Quantization helpers
// ---------------------------------------------------------------------------

/**
 * Encode a float value as a quantized integer according to the given mode.
 * Returns an integer stored in the snapshot and used for delta computation on the wire.
 * Does not handle `float64` (sent as absolute) or `drop-w` (has its own helpers).
 */
function quantizeScalar(v: number, q: QuantizationMode): number {
  if (q.mode === 'scale') return Math.round(v * q.scale);
  if (q.mode === 'range') {
    const steps = q.bits < 32 ? (1 << q.bits) - 1 : 0xFFFF_FFFF;
    return Math.max(0, Math.min(steps, Math.round((v - q.min) / (q.max - q.min) * steps)));
  }
  if (q.mode === 'float16') return floatToFloat16Bits(v);
  if (q.mode === 'float32') return float32ToMonotonic(v);
  return v; // float64 / drop-w — handled by calling code
}

/**
 * Decode a quantized integer back to a float value.
 * Does not handle `float64` or `drop-w`.
 */
function dequantizeScalar(qi: number, q: QuantizationMode): number {
  if (q.mode === 'scale') return qi / q.scale;
  if (q.mode === 'range') {
    const steps = q.bits < 32 ? (1 << q.bits) - 1 : 0xFFFF_FFFF;
    return q.min + (qi / steps) * (q.max - q.min);
  }
  if (q.mode === 'float16') return float16BitsToFloat(qi);
  if (q.mode === 'float32') return monotonicToFloat32(qi);
  return qi; // float64 / drop-w — handled by calling code
}

/**
 * Write a quantized value to the wire for the RPC path (no delta — always absolute).
 * float16/32 write their bit-pattern representation; scale/range use their existing encodings.
 * float64 and drop-w are handled by the calling code.
 */
function writeQuantizedScalar(writer: BinaryWriter, qi: number, q: QuantizationMode): void {
  if (q.mode === 'scale') { writer.writeVarsint(qi); return; }
  if (q.mode === 'range') { writer.writeBits(qi, q.bits); return; }
  if (q.mode === 'float16') { writer.writeBits(qi, 16); return; }
  if (q.mode === 'float32') { writer.writeFloat32(monotonicToFloat32(qi)); return; }
  // float64 / drop-w: caller handles separately
}

/**
 * Read a quantized integer from the wire for the RPC path (no delta — always absolute).
 * Returns the integer representation used by {@link dequantizeScalar}.
 * float64 and drop-w are handled by the calling code.
 */
function readQuantizedScalar(reader: BinaryReader, q: QuantizationMode): number {
  if (q.mode === 'scale') return reader.readVarsint();
  if (q.mode === 'range') return reader.readBits(q.bits);
  if (q.mode === 'float16') return reader.readBits(16);
  if (q.mode === 'float32') return float32ToMonotonic(reader.readFloat32());
  return 0; // float64 / drop-w: caller handles separately
}

// ---------------------------------------------------------------------------
// Drop-W quaternion helpers
// ---------------------------------------------------------------------------

/**
 * Normalise a quaternion and apply the hemisphere convention (w ≥ 0).
 * Returns `[x, y, z]` ready for quantisation in `[-1, 1]`.
 * `w` is always the dropped component and is reconstructed on decode as
 * `sqrt(max(0, 1 − x² − y² − z²))`.
 */
function normaliseDropW(v: { x: number; y: number; z: number; w: number }): [number, number, number] {
  let { x, y, z, w } = v;
  const len = Math.sqrt(x * x + y * y + z * z + w * w);
  if (len > 0) { x /= len; y /= len; z /= len; w /= len; }
  if (w < 0) { x = -x; y = -y; z = -z; }
  return [x, y, z];
}

/**
 * Quantise x, y, z to `bits`-bit unsigned integers in the range `[-1, 1]`.
 * Returns `[qx, qy, qz]`. Captured state format.
 */
function snapshotDropW(v: { x: number; y: number; z: number; w: number }, bits: number): number[] {
  const [x, y, z] = normaliseDropW(v);
  const steps = (1 << bits) - 1;
  const quantise = (c: number) => Math.max(0, Math.min(steps, Math.round((c + 1) / 2 * steps)));
  return [quantise(x), quantise(y), quantise(z)];
}

/**
 * Encode a drop-w quaternion for the replication path (with delta encoding).
 * Captured state: `[qx, qy, qz]`.
 * Wire format: 3 × varsint deltas of quantized x, y, z — no index prefix.
 */
function encodeDropW(
  writer: BinaryWriter,
  v: { x: number; y: number; z: number; w: number },
  bits: number,
  lastArr: number[] | null,
  eps: number,
  nullable: boolean,
): { serialized: number[]; changed: boolean } {
  const qi = snapshotDropW(v, bits);
  const prevQi = lastArr ?? [0, 0, 0];

  const c0 = eps > 0 ? Math.abs(qi[0] - prevQi[0]) > eps : qi[0] !== prevQi[0];
  const c1 = eps > 0 ? Math.abs(qi[1] - prevQi[1]) > eps : qi[1] !== prevQi[1];
  const c2 = eps > 0 ? Math.abs(qi[2] - prevQi[2]) > eps : qi[2] !== prevQi[2];
  const changed = c0 || c1 || c2;

  if (changed) {
    if (nullable) writer.writePresence(true);
    writer.writeVarsint(qi[0] - prevQi[0]);
    writer.writeVarsint(qi[1] - prevQi[1]);
    writer.writeVarsint(qi[2] - prevQi[2]);
  }

  return { serialized: qi, changed };
}

/**
 * Reconstruct `{x, y, z, w}` from a drop-w `[qx, qy, qz]` snapshot.
 */
function reconstructDropW(
  qi: number[],
  bits: number,
  currentValue: unknown,
): { x: number; y: number; z: number; w: number } {
  const steps = (1 << bits) - 1;
  const x = (qi[0] / steps) * 2 - 1;
  const y = (qi[1] / steps) * 2 - 1;
  const z = (qi[2] / steps) * 2 - 1;
  const w = Math.sqrt(Math.max(0, 1 - x * x - y * y - z * z));

  if (currentValue != null) {
    const cv = currentValue as Record<string, unknown>;
    cv.x = x; cv.y = y; cv.z = z; cv.w = w;
    return currentValue as { x: number; y: number; z: number; w: number };
  }
  return { x, y, z, w };
}

/**
 * Decode a drop-w quaternion from the replication binary stream (with delta decoding).
 */
function decodeDropW(
  reader: BinaryReader,
  currentValue: unknown,
  prevArr: number[] | null,
  bits: number,
): { decoded: unknown; captured: unknown } {
  const prevQi = prevArr ?? [0, 0, 0];
  const qi = [
    prevQi[0] + reader.readVarsint(),
    prevQi[1] + reader.readVarsint(),
    prevQi[2] + reader.readVarsint(),
  ];
  const decoded = reconstructDropW(qi, bits, currentValue);
  return { decoded, captured: qi };
}

/**
 * Encode a drop-w quaternion for the RPC path (absolute, no delta).
 * Wire format: 3 × `bits`-bit absolute quantized values.
 */
function encodeRpcDropW(writer: BinaryWriter, v: { x: number; y: number; z: number; w: number }, bits: number): void {
  const qi = snapshotDropW(v, bits);
  const steps = (1 << bits) - 1;
  writer.writeBits(Math.max(0, Math.min(steps, qi[0])), bits);
  writer.writeBits(Math.max(0, Math.min(steps, qi[1])), bits);
  writer.writeBits(Math.max(0, Math.min(steps, qi[2])), bits);
}

/**
 * Decode a drop-w quaternion from the RPC binary stream (absolute, no delta).
 */
function decodeRpcDropW(reader: BinaryReader, bits: number, currentValue: unknown): unknown {
  const qi = [reader.readBits(bits), reader.readBits(bits), reader.readBits(bits)];
  return reconstructDropW(qi, bits, currentValue);
}
